import { Box, Checkbox, CheckboxProps, Flex, Link } from '@chakra-ui/react';
import { useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { b2bCheckboxControlSx, colors } from 'styles/colors';

/** Figma max width; never wider than the card (`w="full"` + this cap). */
const TERMS_ACCEPTANCE_MAX_W = '661px';
const TERMS_ACCEPTANCE_MIN_H = '64px';

export type B2bTermsAcceptanceCheckboxProps = Omit<
  CheckboxProps,
  'isChecked' | 'onChange' | 'children' | 'isIndeterminate'
> & {
  checked: boolean;
  /**
   * UI gate only — this value is **not** part of the B2B registration API payload.
   * Use to disable submit until the user acknowledges terms.
   */
  onChange: (checked: boolean) => void;
  /** Reading direction: checkbox stays on the physical right in RTL rows (all breakpoints). */
  isRtlLayout?: boolean;
};

/**
 * Checkout-style terms acknowledgement for B2B: checkbox + inline link to terms (same tab, {@link ROUTES.termsOfUse}).
 * Does **not** map to an API field; keep it separate from `buildB2BRegistrationRequestBody`.
 */
export const B2bTermsAcceptanceCheckbox = ({
  checked,
  onChange,
  isDisabled,
  isRtlLayout = false,
  ...rest
}: B2bTermsAcceptanceCheckboxProps) => {
  const labelId = useId();
  const handleChange: CheckboxProps['onChange'] = (e) => {
    onChange(e.target.checked);
  };

  const termsCopy = (
    <FormattedMessage
      id='b2b.registration.terms.acknowledge'
      defaultMessage='I have read and agree to the <terms>Terms of use</terms>.'
      values={{
        terms: (chunks) => (
          <Link
            as={RouterLink}
            to={ROUTES.termsOfUse}
            color={colors.b2b.accent}
            textDecoration='underline'
            display='inline'
            position='relative'
            zIndex={1}
            onClick={(e) => e.stopPropagation()}
          >
            {chunks}
          </Link>
        ),
      }}
    />
  );

  const labelOrder = isRtlLayout ? 2 : 1;
  const checkboxOrder = isRtlLayout ? 1 : 2;

  const labelEl = (
    <Box
      id={labelId}
      flex='1'
      minW={0}
      order={labelOrder}
      textStyle='bodySmallMedium'
      color='dark.D800'
      textAlign={isRtlLayout ? 'right' : 'left'}
      alignSelf='center'
      overflowWrap='anywhere'
      wordBreak='break-word'
    >
      {termsCopy}
    </Box>
  );

  const checkboxEl = (
    <Box
      flexShrink={0}
      alignSelf='center'
      display='flex'
      alignItems='center'
      justifyContent='center'
      minW={{ base: '44px', lg: 'unset' }}
      minH={{ base: '44px', lg: 'unset' }}
      lineHeight={0}
      order={checkboxOrder}
    >
      <Checkbox
        aria-labelledby={labelId}
        isChecked={checked}
        onChange={handleChange}
        isDisabled={isDisabled}
        size='md'
        spacing={0}
        sx={{
          ...b2bCheckboxControlSx,
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
          verticalAlign: 'middle',
          '& .chakra-checkbox__label:empty': {
            display: 'none',
            width: 0,
            m: 0,
            p: 0,
          },
        }}
        {...rest}
      />
    </Box>
  );

  return (
    <Flex
      w='full'
      maxW={TERMS_ACCEPTANCE_MAX_W}
      minW={0}
      minH={{
        base: 'auto',
        lg: TERMS_ACCEPTANCE_MIN_H,
      }}
      py={{ base: 2, lg: 0 }}
      gap='10px'
      px={{ base: '14px', lg: '16px' }}
      direction='row'
      alignItems='center'
      flexWrap='nowrap'
      borderRadius='16px'
      borderWidth='1px'
      borderStyle='solid'
      borderColor='#42197740'
      bg='#9D9D9D26'
      opacity={1}
      dir={isRtlLayout ? 'rtl' : 'ltr'}
    >
      {labelEl}
      {checkboxEl}
    </Flex>
  );
};
